/**
 * SitePages — Admin list view for managing page templates (block-based pages).
 *
 * Lists all page instances, supports create / edit / preview / publish / delete.
 * Editing opens the full block builder at /admin/page-builder (existing).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Eye, Edit3, Trash2, Globe, FileText, RefreshCw,
  ExternalLink, CheckCircle, Circle, AlertCircle, Copy,
} from 'lucide-react';
import { Button } from '../../components/new-components/ui/button';
import { Badge } from '../../components/new-components/ui/badge';
import { Input } from '../../components/new-components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/new-components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/new-components/ui/select';
import api from '../../services/api';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */
interface PageRecord {
  id: string;
  title: string;
  slug: string;
  entity_type: string;
  entity_id?: string | null;
  status: 'draft' | 'published';
  nav_visible?: boolean;
  created_at: string;
  updated_at: string;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const unwrap = (res: any): unknown[] => {
  if (Array.isArray(res)) return res;
  const d = res?.data;
  if (Array.isArray(d)) return d;
  if (Array.isArray(d?.data)) return d.data;
  return [];
};

const fmtDate = (d: string) =>
  d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

/* ------------------------------------------------------------------ */
/*  Status badge                                                       */
/* ------------------------------------------------------------------ */
function StatusBadge({ status }: { status: string }) {
  if (status === 'published') {
    return (
      <Badge className="bg-green-50 text-green-700 border-green-200 text-xs">
        <CheckCircle size={11} className="mr-1" /> Published
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-xs text-muted-foreground">
      <Circle size={11} className="mr-1" /> Draft
    </Badge>
  );
}

/* ------------------------------------------------------------------ */
/*  Entity type badge                                                  */
/* ------------------------------------------------------------------ */
function EntityBadge({ entityType }: { entityType?: string }) {
  if (!entityType || entityType === 'site') {
    return <Badge variant="secondary" className="text-xs">Site Page</Badge>;
  }
  if (entityType === 'offering') {
    return <Badge variant="secondary" className="text-xs bg-amber-50 text-amber-700">Offering</Badge>;
  }
  if (entityType === 'property') {
    return <Badge variant="secondary" className="text-xs bg-blue-50 text-blue-700">Property</Badge>;
  }
  return <Badge variant="secondary" className="text-xs">{entityType}</Badge>;
}

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */
const SitePages: React.FC = () => {
  const navigate = useNavigate();

  const [pages, setPages] = useState<PageRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'draft' | 'published'>('all');

  // New page dialog — two-step
  const [showCreate, setShowCreate] = useState(false);
  const [createStep, setCreateStep] = useState<1 | 2>(1);
  const [newTitle, setNewTitle] = useState('');
  const [newEntityType, setNewEntityType] = useState('site');
  const [selectedTemplate, setSelectedTemplate] = useState('generic');
  const [creating, setCreating] = useState(false);

  // Delete confirmation
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  /* ── Load ─────────────────────────────────────────────────────── */
  const loadPages = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/admin/page-templates/components/instances/list');
      setPages(unwrap(res) as PageRecord[]);
    } catch (err: unknown) {
      setError((err as Error)?.message || 'Failed to load pages');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPages();
  }, [loadPages]);

  /* ── Filtered list ───────────────────────────────────────────── */
  const filtered = pages.filter((p) => {
    const q = search.toLowerCase();
    const matchesSearch =
      !q ||
      p.title.toLowerCase().includes(q) ||
      (p.slug || '').toLowerCase().includes(q);
    const matchesStatus =
      statusFilter === 'all' || p.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  /* ── Create page ─────────────────────────────────────────────── */
  const handleCreate = async () => {
    if (!newTitle.trim()) return;
    setCreating(true);
    try {
      // Ensure at least one template exists
      const tplRes = await api.get('/admin/page-templates/components/list');
      let templates = unwrap(tplRes) as Array<{ id: string }>;
      if (templates.length === 0) {
        await api.post('/admin/page-templates/components', {
          name: 'Default',
          category: 'general',
          description: 'Default page template',
          blocks: [],
          settings: {},
        });
        const r2 = await api.get('/admin/page-templates/components/list');
        templates = unwrap(r2) as Array<{ id: string }>;
      }
      const templateId = templates[0]?.id;
      if (!templateId) throw new Error('No template available');

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const res: any = await api.post('/admin/page-templates/components/instances', {
        templateId,
        entityType: newEntityType,
        entityId: null,
        title: newTitle.trim(),
      });
      const created: unknown = res?.data ?? res;
      const createdId = (created as Record<string, string>)?.id;

      // Apply the selected site template
      if (createdId && selectedTemplate !== 'generic') {
        try {
          await api.put(`/admin/page-templates/components/instances/${createdId}`, {
            title: newTitle.trim(),
            blocks: [],
            settings: { siteTemplate: selectedTemplate },
          });
        } catch {
          // non-fatal — editor will fall back to generic
        }
      }

      setShowCreate(false);
      setNewTitle('');
      setNewEntityType('site');
      setSelectedTemplate('generic');
      setCreateStep(1);

      if (createdId) {
        navigate('/admin/page-builder', {
          state: { openPageId: createdId },
        });
      } else {
        loadPages();
      }
    } catch (err: unknown) {
      setError((err as Error)?.message || 'Failed to create page');
    } finally {
      setCreating(false);
    }
  };

  /* ── Delete ──────────────────────────────────────────────────── */
  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/page-templates/components/instances/${deleteId}`);
      setDeleteId(null);
      loadPages();
    } catch (err: unknown) {
      setError((err as Error)?.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  /* ── Duplicate ───────────────────────────────────────────────── */
  const handleDuplicate = async (page: PageRecord) => {
    try {
      await api.post('/admin/page-templates/components/instances', {
        title: `${page.title} (Copy)`,
        slug: `${page.slug}-copy-${Date.now().toString(36)}`,
        entity_type: page.entity_type,
        status: 'draft',
      });
      await loadPages();
    } catch (err) {
      console.error('Duplicate failed', err);
    }
  };

  /* ── Publish toggle ──────────────────────────────────────────── */
  const handlePublishToggle = async (page: PageRecord) => {
    try {
      const newStatus = page.status === 'published' ? 'draft' : 'published';
      await api.put(`/admin/page-templates/components/instances/${page.id}`, {
        status: newStatus,
      });
      loadPages();
    } catch (err: unknown) {
      setError((err as Error)?.message || 'Failed to update status');
    }
  };

  /* ── Render ──────────────────────────────────────────────────── */
  return (
    <div className="max-w-6xl mx-auto space-y-5 sm:space-y-6 min-w-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Site Pages</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Create and manage block-based pages. Open the Page Builder to configure blocks for any page.
          </p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="w-full sm:w-auto">
          <Plus size={15} className="mr-1.5" /> New Page
        </Button>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <span className="flex items-center gap-2">
            <AlertCircle size={14} /> {error}
          </span>
          <button onClick={() => setError('')} className="font-bold">×</button>
        </div>
      )}

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_auto_auto] gap-3 sm:items-center">
        <div className="relative min-w-0 sm:max-w-sm">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search pages…"
            className="pr-4"
          />
        </div>
        <Select
          value={statusFilter}
          onValueChange={(v) => setStatusFilter(v as typeof statusFilter)}
        >
          <SelectTrigger className="w-full sm:w-36">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="published">Published</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="ghost" size="sm" onClick={loadPages} disabled={loading}>
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </Button>
      </div>

      {/* Table */}
      <div className="rounded-lg border border-border overflow-x-auto">
        <table className="w-full min-w-[760px] text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/30">
              <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Name
              </th>
              <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Slug
              </th>
              <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Type
              </th>
              <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Status
              </th>
              <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Updated
              </th>
              <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={6} className="text-center py-16 text-muted-foreground text-sm">
                  <RefreshCw size={20} className="animate-spin mx-auto mb-2" />
                  Loading pages…
                </td>
              </tr>
            )}

            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center py-16">
                  <FileText size={32} className="mx-auto mb-3 text-muted-foreground/30" />
                  <p className="text-sm text-muted-foreground font-medium">No pages found</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {search || statusFilter !== 'all'
                      ? 'Try adjusting your filters.'
                      : 'Click "New Page" to create your first page.'}
                  </p>
                </td>
              </tr>
            )}

            {!loading &&
              filtered.map((page) => (
                <tr key={page.id} className="border-b border-border/50 hover:bg-muted/20 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Globe size={14} className="text-muted-foreground shrink-0" />
                      <span className="font-medium">{page.title}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground font-mono text-xs">
                    {page.slug ? `/${page.slug}` : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <EntityBadge entityType={page.entity_type} />
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={page.status} />
                  </td>
                  <td className="px-4 py-3 text-muted-foreground text-xs">
                    {fmtDate(page.updated_at)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {/* Preview */}
                      {page.slug && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0"
                          title="Preview"
                          onClick={() =>
                            window.open(`/preview/${page.slug}`, '_blank')
                          }
                        >
                          <Eye size={13} />
                        </Button>
                      )}

                      {/* Edit in builder */}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        title="Edit in Page Builder"
                        onClick={() =>
                          navigate('/admin/page-builder', {
                            state: { openPageId: page.id },
                          })
                        }
                      >
                        <Edit3 size={13} />
                      </Button>

                      {/* Publish / Unpublish */}
                      <Button
                        variant="ghost"
                        size="sm"
                        className={`h-7 px-2 text-xs ${page.status === 'published' ? 'text-amber-600' : 'text-green-600'}`}
                        title={page.status === 'published' ? 'Unpublish' : 'Publish'}
                        onClick={() => handlePublishToggle(page)}
                      >
                        {page.status === 'published' ? (
                          <><Circle size={12} className="mr-1" /> Unpublish</>
                        ) : (
                          <><ExternalLink size={12} className="mr-1" /> Publish</>
                        )}
                      </Button>

                      {/* Duplicate */}
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0"
                        onClick={() => handleDuplicate(page)}
                        title="Duplicate page"
                      >
                        <Copy size={14} />
                      </Button>

                      {/* Delete */}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                        title="Delete"
                        onClick={() => setDeleteId(page.id)}
                      >
                        <Trash2 size={13} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-muted-foreground">
        {filtered.length} page{filtered.length !== 1 ? 's' : ''} shown
        {search || statusFilter !== 'all' ? ' (filtered)' : ''}
        {' '}— use the{' '}
        <button
          className="underline hover:text-foreground"
          onClick={() => navigate('/admin/page-builder')}
        >
          Page Builder
        </button>{' '}
        for full editing and template management.
      </p>

      {/* Create Dialog — two-step */}
      <Dialog open={showCreate} onOpenChange={(o) => { if (!o) { setShowCreate(false); setCreateStep(1); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {createStep === 1 ? 'New Page' : 'Choose a Starting Template'}
            </DialogTitle>
          </DialogHeader>

          {createStep === 1 && (
            <>
              <div className="space-y-4 py-2">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">Page Title</label>
                  <Input
                    value={newTitle}
                    onChange={(e) => setNewTitle(e.target.value)}
                    placeholder="e.g. About Us, Investment Offering, Contact"
                    onKeyDown={(e) => e.key === 'Enter' && newTitle.trim() && setCreateStep(2)}
                    autoFocus
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">Page Type</label>
                  <Select value={newEntityType} onValueChange={setNewEntityType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="site">Site Page (marketing / landing)</SelectItem>
                      <SelectItem value="offering">Offering Page</SelectItem>
                      <SelectItem value="property">Property Page</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
                <Button onClick={() => setCreateStep(2)} disabled={!newTitle.trim()}>
                  Next →
                </Button>
              </DialogFooter>
            </>
          )}

          {createStep === 2 && (
            <>
              <div className="py-2">
                <p className="text-xs text-muted-foreground mb-3">
                  Select a layout to pre-populate your page structure. You can fully customize it in the builder.
                </p>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {[
                    { id: 'landing', emoji: '🚀', label: 'Landing', desc: 'Hero, features, CTA' },
                    { id: 'about',   emoji: '🏢', label: 'About',   desc: 'Story, team, values' },
                    { id: 'contact', emoji: '✉️',  label: 'Contact', desc: 'Form, location, FAQ' },
                    { id: 'funnel',  emoji: '🎯', label: 'Funnel',  desc: 'Lead capture, offer' },
                    { id: 'team',    emoji: '👥', label: 'Team',    desc: 'Bios and roles' },
                    { id: 'generic', emoji: '📄', label: 'Blank',   desc: 'Start from scratch' },
                  ].map(tpl => (
                    <button
                      key={tpl.id}
                      onClick={() => setSelectedTemplate(tpl.id)}
                      className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${
                        selectedTemplate === tpl.id
                          ? 'border-primary bg-primary/10'
                          : 'border-border hover:border-primary/50 hover:bg-muted/30'
                      }`}
                    >
                      <span className="text-2xl">{tpl.emoji}</span>
                      <div className="text-center">
                        <p className="text-sm font-semibold">{tpl.label}</p>
                        <p className="text-[10px] text-muted-foreground leading-tight mt-0.5">{tpl.desc}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setCreateStep(1)}>← Back</Button>
                <Button onClick={handleCreate} disabled={creating}>
                  {creating ? 'Creating…' : 'Create & Open Builder'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Page</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            This will permanently delete the page and all its block configuration. This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? 'Deleting…' : 'Delete Page'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SitePages;
